import React, { useState, useEffect, useCallback, useContext } from 'react'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemText,
} from '@material-ui/core'
import { ArrowBackIos, PhoneIphone } from '@material-ui/icons'
import { Link, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { ModalContext } from '@root-gipro/context/modal-context'
import { DeleteProjectWindow, SelectF20Window } from '@root-gipro/modules/userProjects/components/Project/Modal'
import { PrimaryBtn, SecondaryBtn } from '@root-gipro/shared/Button/Button'
import Loader from '@root-gipro/shared/Loader/Loader'
import TabsCheckPlate from './TabsCheckPlate'

import {
	copyProject,
	deleteUserProject,
	downloadForm20,
	saveUserProject,
	setCopyAlert,
	setLocked,
} from '@root-gipro/modules/userProjects/store/actions'
import { showNotify } from '@root-gipro/modules/notify/store/actions'
import store from '@root-gipro/store'

interface Props {
	projectObj: any
	originalObj: any
	typePageUrl: string
	versionIpr?: { version_name?: string; created_at?: number }
	author?: string
	authorTel?: string
	timestampToDate: (ts: number) => string
}

export const UserProjectToolbar: React.FC<Props> = ({
	projectObj,
	originalObj,
	typePageUrl,
	versionIpr,
	author,
	authorTel,
	timestampToDate,
}) => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { anchorEl, setAnchorEl, setAccessory } = useContext(ModalContext)
	const [isOpenIprCopy, setIsOpenIprCopy] = useState(false)
	const [selectedVersionForCopy, setSelectedVersionForCopy] = useState<any>(null)
	const [showReplaceDialog, setShowReplaceDialog] = useState(false)
	const [pendingCopy, setPendingCopy] = useState(false)
	const copyAlert = useSelector((state: any) => state.userProjects.copyAlert)
	const company = useSelector((state: any) => state.public.company)
	const isAdmin = useSelector((state: any) => state.auth.user.isAdmin)
	const [companyIprList, setCompanyIprList] = useState<any[]>([])
	const [isAuthorProject, setIsAuthorProject] = useState<boolean>(false)
	const [isLocked, setIsLocked] = useState<boolean>(false)

	const { id } = store.getState().auth.user

	useEffect(() => {
		setIsAuthorProject(projectObj.userId === id)
		setIsLocked(originalObj.locked)
	}, [projectObj, originalObj])

	useEffect(() => {
		if (projectObj.companyId) {
			const iprs = company.find((c: any) => c.id === projectObj.companyId)?.ipr || []
			setCompanyIprList(iprs.slice().sort((a: any, b: any) => a.created_at - b.created_at))
		}
	}, [projectObj.companyId, company])

	// Отслеживаю изменение `copyAlert` и показываю диалог только когда данные обновились
	useEffect(() => {
		if (pendingCopy && copyAlert === 'alert') {
			setShowReplaceDialog(true)
			setPendingCopy(false)
		} else if ((pendingCopy && !copyAlert) || copyAlert === 'success') {
			setIsOpenIprCopy(false)
			setPendingCopy(false)
		}
	}, [copyAlert, pendingCopy])

	const onOpenIprCopy = () => {
		setIsOpenIprCopy(true)
		setSelectedVersionForCopy(
			company.find((c: any) => c.id === projectObj.companyId)?.ipr.find((v: any) => v.id === projectObj.ipr_id)
		)
	}

	const onCloseIprCopy = () => {
		setIsOpenIprCopy(false)
		dispatch(setCopyAlert(null))
	}

	const handleCopy = () => {
		if (selectedVersionForCopy) {
			setPendingCopy(true)
			dispatch(copyProject(projectObj.id, projectObj.code, selectedVersionForCopy.id))
		}
	}

	const handleConfirmReplace = () => {
		setShowReplaceDialog(false)
		dispatch(copyProject(projectObj.id, projectObj.code, selectedVersionForCopy.id, 1))
		dispatch(setCopyAlert(null))
	}

	const handlerToArchive = () => {
		if ((!originalObj.locked && isAdmin) || (!originalObj.locked && !isAdmin && isAuthorProject)) {
			const { author, ...projectObjData } = projectObj
			const clone = Object.assign({}, projectObjData)
			clone.archive = 1
			delete clone.id
			delete clone.userId
			dispatch(saveUserProject(clone, false, projectObjData.id))
		} else if (!originalObj.locked && !isAdmin && !isAuthorProject) {
			dispatch(
				showNotify({
					type: 'warning',
					message: `У вас есть доступ для переноса в архив расчетов, созданных вами. Для переноса расчета в архив обратитесь к автору расчета или администратору`,
				})
			)
		} else if (originalObj.locked) {
			if (isAdmin) {
				dispatch(
					showNotify({
						type: 'warning',
						message: `Нельзя переместить в архив данный расчета, так как он заблокирован. Для переноса сначала разблокируйте расчет`,
					})
				)
			} else {
				dispatch(
					showNotify({
						type: 'warning',
						message: `Нет прав на перемещение расчета. Для переноса расчета в архив обратитесь к администратору`,
					})
				)
			}
		}
	}

	const handlerFromArchive = () => {
		if ((!originalObj.locked && isAdmin) || (!originalObj.locked && !isAdmin && isAuthorProject)) {
			const { author, ...projectObjData } = projectObj
			const clone = Object.assign({}, projectObjData)
			clone.archive = 0
			delete clone.id
			delete clone.userId
			dispatch(saveUserProject(clone, false, projectObjData.id))
		} else if (!originalObj.locked && !isAdmin && !isAuthorProject) {
			dispatch(
				showNotify({
					type: 'warning',
					message: `У вас есть доступ для переноса расчетов, созданных вами. Для переноса расчета обратитесь к администратору`,
				})
			)
		} else if (originalObj.locked) {
			if (isAdmin) {
				dispatch(
					showNotify({
						type: 'warning',
						message: `Нельзя переместить данный расчета, так как он заблокирован. Для переноса сначала разблокируйте расчет`,
					})
				)
			} else {
				dispatch(
					showNotify({
						type: 'warning',
						message: `Нет прав на перемещение расчета. Для переноса расчета обратитесь к администратору`,
					})
				)
			}
		}
	}

	const openWindow = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			setAnchorEl?.(e.currentTarget)
			setAccessory?.('check-list')
		},
		[setAnchorEl, setAccessory]
	)

	const handlerClickDownloadF20Popup1 = (version: string) => {
		dispatch(downloadForm20(projectObj.id, projectObj.code, version))
		setAnchorEl?.(null)
	}

	const handlerClickDownloadF20Popup2 = () => {
		setAnchorEl?.(null)
	}

	const handlerClickDeletePopup = () => {
		dispatch(deleteUserProject(projectObj.id, projectObj.code))
		setAnchorEl?.(null)
	}
	const handlerLockUserProject = () => {
		if (isAdmin) {
			dispatch(setLocked(!projectObj.locked))
			if (!projectObj.locked === true) {
				dispatch(
					showNotify({
						type: 'success',
						message: `Расчет заблокирован, для применения изменений сохраните расчет`,
					})
				)
			} else {
				dispatch(
					showNotify({
						type: 'success',
						message: `Расчет разблокирован, для применения изменений сохраните расчет`,
					})
				)
			}
			const { author, ...projectObjData } = projectObj
			const clone = Object.assign({}, projectObjData)
			clone.locked = !projectObj.locked
			delete clone.id
			delete clone.userId
			dispatch(saveUserProject(clone, false, projectObjData.id))
		} else {
			dispatch(
				showNotify({
					type: 'warning',
					message: `Нельзя заблокировать или разблокировать расчет. Для изменения статуса расчета обратитесь к администратору`,
				})
			)
		}
	}

	useEffect(() => {
		if (isOpenIprCopy == false) {
			dispatch(setCopyAlert(null))
		}
	}, [isOpenIprCopy])

	return (
		<Box p={2} className='container'>
			<Box display='flex' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap' style={{ gap: 16 }}>
				{/* Кнопка Назад */}
				<Button
					component={Link}
					to='/user-projects'
					startIcon={<ArrowBackIos />}
					style={{ textTransform: 'none', color: '#1baaf0', padding: 0, minWidth: 0 }}
				>
					Назад
				</Button>

				{/* Блок с кнопками действий */}
				{projectObj?.id && (
					<Box display='flex' alignItems='center' flexWrap='wrap' className='user-projects__options' style={{ gap: 8 }}>
						<IconButton data-popup='select-f20' onClick={() => handlerClickDownloadF20Popup1('newPlus')}>
							<span className='download-icon' title='Скачать Форму 20'></span>
						</IconButton>
						<IconButton onClick={onOpenIprCopy}>
							<span className='copy-icon' title='Копировать'></span>
						</IconButton>
						<IconButton
							data-popup='del-project'
							onClick={
								typePageUrl.includes('user-archive')
									? openWindow
									: () => {
											dispatch(
												showNotify({
													type: 'warning',
													message: `Для удаления проекта необходимо перенести его в Архив`,
												})
											)
									  }
							}
						>
							<span className='delete-icon' title='Удалить'></span>
						</IconButton>
						{!typePageUrl.includes('user-archive') ? (
							<PrimaryBtn onClick={handlerToArchive}>Перенести в Архив →</PrimaryBtn>
						) : (
							<PrimaryBtn onClick={handlerFromArchive}>← Перенести в Проекты</PrimaryBtn>
						)}
						<Box display='flex' alignItems='center' style={{ gap: 12 }}>
							<button
								style={{ overflow: 'hidden', maxWidth: 32, width: '100%' }}
								onClick={() => handlerLockUserProject()}
								disabled={!originalObj.id}
							>
								<div className={`${projectObj.locked ? 'lock-icon' : 'unlock-icon'}`}></div>
							</button>
						</Box>
					</Box>
				)}
			</Box>

			<Grid container spacing={3} alignItems='flex-start' style={{ marginTop: 16 }}>
				<Grid item xs={12} style={!projectObj?.id ? { display: 'flex', justifyContent: 'center' } : {}}>
					{projectObj?.id ? (
						<Box
							display='flex'
							alignItems='center'
							justifyContent='space-between'
							flexWrap='wrap'
							style={{ width: '100%', gap: 16 }}
						>
							<Box>
								{versionIpr?.version_name && (
									<div className='cell-label author-label'>
										<span style={{ color: 'gray' }}>Версия ИПР:</span>
										<span className='ml-8'>{versionIpr.version_name}</span>
										{versionIpr.created_at && (
											<span className='ml-8'>{new Date(versionIpr.created_at * 1000).toLocaleDateString()}</span>
										)}
									</div>
								)}

								{projectObj?.created_at && (
									<div className='cell-label author-label'>
										<span style={{ color: 'gray' }}>Добавлен:</span>
										<span className='ml-8'>{timestampToDate(projectObj.created_at)}</span>
									</div>
								)}
							</Box>

							<TabsCheckPlate />

							{(author || authorTel) && (
								<Box
									className='cell-label author-label'
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-start',
										maxWidth: 300,
										gap: '12px',
									}}
								>
									<Box style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', overflow: 'hidden' }}>
										<span style={{ color: 'gray', flexShrink: 0 }}>Автор расчета:</span>
										{author && (
											<span
												className='ml-8'
												style={{
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												{author}
											</span>
										)}
									</Box>

									{authorTel && (
										<Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
											<PhoneIphone fontSize='small' color='disabled' />
											<span className='ml-2'>{authorTel}</span>
										</Box>
									)}
								</Box>
							)}
						</Box>
					) : (
						// аналогично для блока без projectObj.id
						<Box
							className='cell-label author-label'
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								maxWidth: 300,
								gap: '12px',
							}}
						>
							{/* <Box style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', overflow: 'hidden' }}>
								<span style={{ color: 'gray', flexShrink: 0 }}>Автор расчета:</span>
								{author && (
									<span
										className='ml-8'
										style={{
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										}}
									>
										{author}
									</span>
								)}
							</Box> */}
							<TabsCheckPlate />
							{/* {authorTel && (
								<Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
									<PhoneIphone fontSize='small' color='disabled' />
									<span className='ml-2'>{authorTel}</span>
								</Box>
							)} */}
						</Box>
					)}
				</Grid>
			</Grid>

			{/* Dialogs */}
			<Dialog open={isOpenIprCopy} onClose={onCloseIprCopy}>
				<DialogTitle>Выберите версию ИПР</DialogTitle>
				<DialogContent>
					<List style={{ height: '250px' }}>
						{companyIprList.length ? (
							companyIprList.map((version: any) => (
								<React.Fragment key={version.id}>
									<ListItem
										selected={selectedVersionForCopy?.id === version.id}
										onClick={() => setSelectedVersionForCopy(version)}
										style={{ cursor: 'pointer' }}
									>
										<ListItemText primary={version.version_name} />
									</ListItem>
									<Divider />
								</React.Fragment>
							))
						) : (
							<Loader />
						)}
					</List>
				</DialogContent>
				<DialogActions style={{ justifyContent: 'space-evenly' }}>
					<PrimaryBtn disabled={!selectedVersionForCopy} onClick={handleCopy}>
						Копировать
					</PrimaryBtn>
					<SecondaryBtn onClick={onCloseIprCopy}>Отмена</SecondaryBtn>
				</DialogActions>
			</Dialog>

			<Dialog open={showReplaceDialog} onClose={() => setShowReplaceDialog(false)}>
				<DialogTitle>Подтверждение замены</DialogTitle>
				<DialogContent>
					В версии ИПР <strong>{selectedVersionForCopy?.version_name}</strong> уже существует проект с идентификатором{' '}
					<strong>{projectObj?.code}</strong>. Заменить его?
				</DialogContent>
				<DialogActions>
					<PrimaryBtn onClick={handleConfirmReplace}>Заменить</PrimaryBtn>
					<SecondaryBtn onClick={() => setShowReplaceDialog(false)}>Отмена</SecondaryBtn>
				</DialogActions>
			</Dialog>

			{anchorEl?.dataset.popup === 'del-project' && (
				<DeleteProjectWindow anchorEl={anchorEl} setAnchorEl={setAnchorEl!} handler={handlerClickDeletePopup} />
			)}

			{anchorEl?.dataset.popup === 'select-f20' && (
				<SelectF20Window
					anchorEl={anchorEl}
					setAnchorEl={setAnchorEl!}
					handler1={handlerClickDownloadF20Popup1}
					handler2={handlerClickDownloadF20Popup2}
					version={originalObj?.unc_sort}
				/>
			)}
		</Box>
	)
}
